// Legal documents — DRAFTS pending review by an Australian-qualified legal practitioner.
// Replace with lawyer-reviewed content before flipping publicRegistrationEnabled to true.
// All seven documents render at /legal in the Reservo app.

export const TERMS_OF_USE = `TERMS OF USE — RESERVO PLATFORM

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

These Terms of Use ("Terms") govern your access to and use of the Reservo platform — the Reservo website at reservo.au, the Reservo mobile and web applications, and any related services (collectively, the "Platform") operated by David Williams (ABN 50 639 154 277) trading as Reservo ("we", "us", "our", "Reservo").

By accessing or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.

1. ABOUT RESERVO

1.1 Reservo is a digital booking platform that connects riders ("Riders") with independent driver-partners ("Drivers") for pre-booked point-to-point transport services in New South Wales, Australia.

1.2 Reservo's authorisation as a Booking Service Provider under the Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW) is currently pending — application in preparation. The Platform is in pre-launch testing and is not open to the general public until authorisation is granted.

1.3 Reservo is a technology platform. Reservo is not a transport provider. Drivers are independent contractors who provide transport services using their own vehicles, in accordance with their own driver authorisation issued under the Act.

1.4 The Platform is designed to facilitate pre-booked rides only, scheduled between 1 hour and 30 days in advance. Reservo does not provide on-demand or rank-and-hail services.

1.5 Reservo is not an insurer and does not provide insurance. Each Driver is an independent contractor who must hold compulsory third-party (CTP) insurance and any other insurance required to provide point-to-point transport in NSW. Transport services are covered by the relevant Driver’s insurance, not by Reservo.

2. ELIGIBILITY

2.1 You may use the Platform only if you are at least 18 years old and capable of entering into a legally binding contract under Australian law.

2.2 Minors may travel as passengers only when accompanied by a Rider account holder who is over 18 and has accepted responsibility for the booking, including any unaccompanied-minor restrictions communicated through the Platform.

2.3 You must provide accurate, current, and complete information when registering an account, and keep that information updated.

3. ACCOUNT REGISTRATION AND SECURITY

3.1 To book rides or drive on the Platform, you must register an account. Each account is personal to you. You may not share login credentials, sell, transfer, or assign your account to any other person.

3.2 You are responsible for all activity occurring under your account, including maintaining the confidentiality of your password and security PIN.

3.3 Reservo may verify your identity by SMS, email, government-issued identification, or other means. Reservo may suspend, restrict, or terminate accounts that cannot be verified or that breach these Terms.

4. BOOKINGS AND FARES

4.1 Riders may book a ride by selecting a pickup location, destination, scheduled pickup time (between 1 hour and 30 days ahead), service tier, and (optionally) a preferred Driver. The Platform displays the fare estimate before booking, calculated based on time, distance, service tier, applicable tolls, NSW Passenger Service Levy, and GST.

4.2 Once a booking is confirmed and a Driver assigned, the fare is fixed unless the booking is modified by the Rider. Fare modifications mid-trip (route changes, additional stops, waiting time beyond the free window) may apply additional charges as displayed in the Platform.

4.3 The Rider's payment method on file is authorised at booking and charged on trip completion. Payment processing is performed by Stripe Payments Australia Pty Ltd. Reservo does not store full card numbers.

4.4 The NSW Passenger Service Levy applies to every fare and is collected by Reservo and remitted to the NSW Government as required by law.

4.5 Some fares include a peak-period surcharge, applied automatically based on the scheduled pickup time and shown in the fare estimate before you confirm a booking. Tolls and surcharges shown at booking are estimates and may be reconciled to the actual amounts incurred on the trip.

5. CANCELLATIONS

5.1 Riders may cancel a booking at any time. Cancellations more than 24 hours before scheduled pickup are free of charge. Cancellations within 24 hours of scheduled pickup incur a $10 cancellation fee. Cancellations within 30 minutes of scheduled pickup, or after the Driver has departed for the pickup, incur a $25 cancellation fee plus any toll or distance the Driver has incurred.

5.2 Drivers may cancel a confirmed booking, but cancellations within 2 hours of scheduled pickup result in penalties to the Driver under the Driver Agreement.

5.3 If a Driver fails to arrive at the scheduled pickup time without lawful excuse, the Rider is entitled to cancel without charge and may be eligible for compensation as set out in the Platform.

5.4 Full cancellation and refund terms are in the Reservo Cancellation and Refund Policy.

6. CONDUCT

6.1 You agree not to use the Platform to:
  (a) commit any criminal offence;
  (b) harass, threaten, defame, or discriminate against any other user;
  (c) damage Driver vehicles or transport prohibited items;
  (d) circumvent the Platform's payment, fare, or matching systems;
  (e) interfere with the Platform's operation or security;
  (f) attempt to take bookings off-platform after being introduced through Reservo.

6.2 Reservo may take action against accounts that breach this clause, including suspension, termination, charges for damage, and reporting to authorities.

7. SAFETY

7.1 Reservo provides Trip PIN verification, GPS trip tracking, in-app SOS feature, and complaints channels in line with the safety obligations of a Booking Service Provider under the Act.

7.2 In the event of an emergency, contact 000 first, then notify Reservo via the SOS feature.

7.3 Reservo cooperates with NSW Police, the Point to Point Transport Commissioner, and other authorities as required by law.

8. AUDIO RECORDING

8.1 Reservo does not record audio during trips. If this changes in the future, Riders and Drivers will be notified in advance and consent will be re-collected before any recording occurs. The current position is set out in the Audio Recording Consent document.

9. INTELLECTUAL PROPERTY

9.1 The Platform, including its software, design, branding, and content (other than user-generated content), is owned by Reservo or its licensors and is protected by Australian and international copyright, trade mark, and other intellectual property laws.

9.2 You are granted a limited, non-exclusive, non-transferable, revocable licence to access and use the Platform for its intended purposes only.

9.3 You retain ownership of content you submit (e.g. ratings, reviews, support messages) but grant Reservo a worldwide, royalty-free, sub-licensable licence to use, modify, and display that content for operating and promoting the Platform.

10. PRIVACY

10.1 Reservo's collection and handling of personal information is set out in the Reservo Privacy Policy, which forms part of these Terms.

11. DISCLAIMERS

11.1 Reservo provides the Platform on an "as available" basis. To the maximum extent permitted by law, Reservo does not warrant that the Platform will be uninterrupted, error-free, or secure, or that Drivers will perform to any specific standard.

11.2 Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy under the Australian Consumer Law (Schedule 2 to the Competition and Consumer Act 2010 (Cth)) that cannot be excluded.

11.3 Where Reservo is liable for breach of a non-excludable consumer guarantee in connection with services, Reservo's liability is limited (where permitted) to the supply of those services again or the cost of the supply of those services again.

12. LIMITATION OF LIABILITY

12.1 Subject to clause 11.2, Reservo is not liable for any indirect, incidental, special, consequential, or exemplary loss, including loss of profit, revenue, data, or goodwill, arising out of or in connection with your use of the Platform.

12.2 Reservo's aggregate liability arising out of or in connection with these Terms, in any 12-month period, is limited to the total fees paid by you to Reservo in that period.

13. INDEMNITY

13.1 You agree to indemnify Reservo against any loss, damage, claim, or cost (including legal costs on a solicitor-client basis) arising out of your breach of these Terms, your misuse of the Platform, or your violation of the rights of any third party.

14. TERMINATION

14.1 You may close your account at any time by contacting admin@reservo.au.

14.2 Reservo may suspend or terminate your account immediately and without notice if you breach these Terms, if your account presents a safety or fraud risk, or if Reservo is required to do so by law or by an authority.

14.3 Provisions of these Terms which by their nature survive termination (including intellectual property, indemnity, limitation of liability, and dispute resolution) survive termination.

15. CHANGES TO THESE TERMS

15.1 Reservo may update these Terms from time to time. The current version is always available at reservo.au/legal. Material changes will be notified to active users by email or in-app notification at least 14 days before they take effect.

15.2 Your continued use of the Platform after changes take effect constitutes acceptance of the updated Terms.

16. DISPUTE RESOLUTION

16.1 If you have a complaint, contact admin@reservo.au in the first instance. Reservo will respond within 5 business days and aim to resolve the complaint within 21 business days.

16.2 If you are not satisfied with Reservo's response, you may refer the matter to:
  (a) the Point to Point Transport Commissioner (pointtopoint.nsw.gov.au) for matters relating to point-to-point transport service quality or safety;
  (b) the NSW Civil and Administrative Tribunal (NCAT) for consumer-law disputes;
  (c) the Office of the Australian Information Commissioner (OAIC) for privacy complaints.

17. GENERAL

17.1 Entire agreement: These Terms, together with the Privacy Policy and any agreement applicable to your role (Rider Agreement, Driver Agreement, or Corporate Account Terms), form the entire agreement between you and Reservo on their subject matter and supersede any prior understanding.

17.2 Severability: If any provision of these Terms is found to be invalid or unenforceable, it is severed to the minimum extent necessary and the remaining provisions continue in force.

17.3 Waiver: A failure or delay by Reservo in exercising a right under these Terms is not a waiver of that right, and a single or partial exercise does not prevent any further exercise.

17.4 Assignment: You may not assign or transfer your rights under these Terms without our written consent. Reservo may assign these Terms to a related body corporate, or in connection with a sale or restructure of the business, on notice to you.

17.5 Notices: We may give you notices through the Platform, by email, or by SMS to the contact details on your account. You are responsible for keeping those details current.

17.6 Force majeure: Reservo is not liable for any failure or delay in performing its obligations caused by events beyond its reasonable control, including natural disasters, communications or power failures, third-party service outages, industrial action, or government action.

17.7 No agency: Nothing in these Terms creates a partnership, employment, agency, or joint venture between you and Reservo beyond the limited booking and payment role described in clause 1.

18. GOVERNING LAW

18.1 These Terms are governed by the laws of New South Wales, Australia. You and Reservo submit to the exclusive jurisdiction of the courts of New South Wales.

19. CONTACT

David Williams trading as Reservo
ABN: 50 639 154 277
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner. Final terms will be issued before public launch and may differ materially. Submit any questions to admin@reservo.au.`;


export const PRIVACY_POLICY = `PRIVACY POLICY — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

David Williams (ABN 50 639 154 277) trading as Reservo ("Reservo", "we", "us", "our") is committed to protecting your privacy and complying with the Privacy Act 1988 (Cth), the Australian Privacy Principles ("APPs"), the Notifiable Data Breaches scheme, and applicable NSW and Commonwealth law.

This Privacy Policy explains what personal information we collect, how we use, disclose, and protect it, and how you can exercise your privacy rights.

1. WHAT WE COLLECT

1.1 Identification and account information: full name, date of birth (where required), email address, mobile phone number, password (stored hashed), security PIN (stored hashed), profile photo (Drivers only).

1.2 Driver-specific information: NSW driver authorisation details, driver licence, vehicle registration, vehicle insurance, ABN, working with children check (if applicable), criminal history check, training records, fatigue records, banking details for payouts (handled by Stripe).

1.3 Booking and trip information: pickup and destination addresses, scheduled pickup time, service tier, fare details, route, GPS location during active trips, Trip PIN, ratings and reviews, in-app messages between Rider and Driver, complaint records. For multi-stop bookings, this also includes the addresses of each intermediate stop and the first name only of any additional passengers ("co-riders") you arrange to be picked up. Reservo does not collect phone numbers or further identifying information about co-riders. For recurring bookings, we also collect the recurrence pattern (which days/dates and times you have scheduled), the preferred Driver identity (when one is established), and per-occurrence payment status records. For multi-vehicle bookings, we collect optional passenger-group labels you assign to each vehicle, per-vehicle Trip PINs, and per-vehicle payment and Driver matching status. We do not collect names or contact details of passengers in multi-vehicle bookings other than yourself as booking rider.

1.4 Payment information: card details (handled and stored by Stripe — Reservo does not store full card numbers), transaction history, wallet balance and transactions, referral and promotional code use.

1.5 Device and technical information: device type, operating system, browser, IP address, app version, crash logs, usage analytics.

1.6 Communications: emails, SMS messages, support tickets, complaint records, audit log entries.

1.7 Reservo does not currently record audio during trips. Voice recordings are not collected.

1.8 Photographs that you submit through the Platform (including lost item report photos, damage claim evidence photos and receipts, incident report photos, vehicle inspection photos, and any other photos you upload to the Platform) are stored on secure Google Cloud (Firebase Storage) infrastructure. Stored photos are access-controlled at the file level: each photo is only readable by the parties directly involved in the matter (e.g. the Rider and Driver on a lost item case) and Reservo administrators with appropriate access. Photographs are retained for 7 years from the closure of the matter to which they relate, after which they are permanently deleted.

2. HOW WE COLLECT IT

2.1 Directly from you when you register, book, drive, message support, respond to surveys, or otherwise interact with the Platform.

2.2 Automatically through your use of the Platform (location data, device data, usage logs).

2.3 From third parties:
  (a) the Australian Business Register (ABN verification);
  (b) NSW point-to-point transport authorisation databases;
  (c) Stripe (payment processing);
  (d) Google Maps (geocoding and routing);
  (e) ClickSend (SMS delivery);
  (f) credit, identity, or background-check providers (Drivers only, with consent).

3. WHY WE COLLECT IT — PRIMARY PURPOSES

3.1 To provide the Platform: matching Riders to Drivers, calculating fares, processing payments, navigating routes, recording trip events.

3.2 To verify identity and authorisation status of Riders and Drivers as required by the Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW).

3.3 To ensure safety: GPS tracking, Trip PIN verification, SOS handling, fatigue management, incident response, cooperation with police and the Point to Point Transport Commissioner.

3.4 To process payments and remit the NSW Passenger Service Levy.

3.5 To resolve disputes and complaints, and to comply with our legal obligations.

3.6 To improve the Platform — analytics, debugging, and product development (using de-identified or aggregated data wherever possible).

3.7 To communicate with you about your account, trips, important updates, and (where you consent) marketing.

4. WHO WE SHARE IT WITH

4.1 Riders and Drivers see limited information about each other:
  (a) Riders see Drivers' first name and last initial, vehicle make, model, colour, and registration plate, and aggregated rating;
  (b) Drivers see Riders' full name (necessary for safety and pickup verification);
  (c) Phone numbers are not shared between Riders and Drivers; in-app messaging is provided instead, with content filtering and time-window restrictions.

4.2 Service providers (under written confidentiality and data-handling agreements):
  (a) Google Cloud / Firebase (hosting, database, authentication) — primary data is stored in the australia-southeast1 (Sydney) region; some operational metadata may be processed in other Google Cloud regions;
  (b) Stripe Payments Australia Pty Ltd (payment processing) — Stripe stores data in accordance with its own privacy policy and PCI-DSS obligations;
  (c) ClickSend (SMS delivery);
  (d) Google Maps (routing).

4.3 Authorities and regulators where required or permitted by law:
  (a) NSW Police, courts, and tribunals;
  (b) Point to Point Transport Commissioner;
  (c) Office of the Australian Information Commissioner;
  (d) the Australian Taxation Office;
  (e) any other regulator with valid jurisdiction.

4.4 Other parties:
  (a) corporate account administrators (if you book under a corporate account);
  (b) auditors, insurers, and legal advisers, where reasonably necessary;
  (c) prospective purchasers of the business (under confidentiality agreements);
  (d) emergency services in life-safety situations.

4.5 Photographs you submit through the Platform may be visible to the other party involved in the matter to which the photograph relates (e.g. lost item photographs are visible to your Driver; damage claim photographs are visible to the affected Rider once a decision has been made). Reservo administration has access to all submitted photographs for the purposes of reviewing claims, complaints, and incidents.

4.6 We do not sell your personal information.

5. CROSS-BORDER DISCLOSURE

5.1 Some of our service providers (Google, Stripe) may store or process data outside Australia, including in the United States and the European Union. By using the Platform, you acknowledge this. We take reasonable steps to ensure overseas recipients comply with the APPs or substantially similar standards.

6. HOW WE PROTECT IT

6.1 Encryption in transit (TLS) and at rest where feasible.

6.2 Access controls — Reservo staff access to personal information is restricted, logged, and audited.

6.3 PIN, password, and API key hashing.

6.4 Audit logging of administrative actions.

6.5 Regular security review and Notifiable Data Breach response procedures.

6.6 No system is perfectly secure. If a data breach occurs that is likely to result in serious harm, we will notify affected individuals and the OAIC as required by the Notifiable Data Breaches scheme.

7. HOW LONG WE KEEP IT

7.1 Account data: while your account is active, plus 7 years after closure (consistent with taxation and corporate record-keeping obligations).

7.2 Trip records (including GPS, ratings, and messaging): 7 years (consistent with point-to-point industry record-keeping standards and limitation periods for civil claims).

7.3 Support tickets, complaints, and audit log: 7 years.

7.4 Marketing data: until you unsubscribe, then archived for proof-of-consent purposes.

7.5 We may retain de-identified or aggregated data indefinitely for analytics and research.

8. YOUR RIGHTS

8.1 You may, at any time:
  (a) ask what personal information we hold about you;
  (b) request correction of inaccurate information;
  (c) request deletion or de-identification, subject to our legal obligations to retain certain records;
  (d) withdraw consent for marketing communications;
  (e) lodge a complaint about how we handle your personal information.

8.2 To exercise these rights, contact admin@reservo.au. We will respond within 30 days.

8.3 If you are not satisfied with our response, you may complain to the Office of the Australian Information Commissioner (oaic.gov.au, 1300 363 992).

9. COOKIES AND ANALYTICS

9.1 The Platform uses cookies and local storage for authentication, session management, and analytics. You can disable cookies in your browser, but the Platform may not function correctly without them.

10. CHILDREN

10.1 The Platform is not intended for use by children under 18 to register accounts. Minors may travel as passengers only under an adult Rider's account.

11. ADDITIONAL PRIVACY MATTERS

11.1 Dealing with us anonymously or by pseudonym (APP 2): Where it is lawful and practicable, you may deal with us anonymously or by using a pseudonym — for example, when making a general enquiry. However, we cannot provide transport bookings, verify identity and authorisation, process payments, or meet our safety obligations under the Point to Point Transport (Taxis and Hire Vehicles) Act 2016 (NSW) without identifying you, so a Reservo account cannot be created or used anonymously.

11.2 Government-related identifiers (APP 9): We collect government identifiers (such as a NSW driver licence number or an ABN) only where reasonably necessary to verify authorisation, process payouts, or meet a legal obligation. We do not use or disclose a government identifier as our own means of identifying you, and we do not adopt it as your account identifier.

11.3 Automated decision-making: Reservo uses automated processes to operate the Platform — including matching Riders to Drivers, scoring and ranking route options, calculating fares (including any peak-period surcharge), and detecting suspected fraud or breaches of our policies. These processes do not, on their own, make legally significant decisions about you without the opportunity for human review. If an automated process restricts your account or declines a payment, you may ask us to review it by contacting admin@reservo.au.

12. CHANGES TO THIS POLICY

12.1 We may update this Privacy Policy from time to time. The current version is always at reservo.au/legal. Material changes will be notified at least 14 days in advance.

13. CONTACT — PRIVACY OFFICER

David Williams trading as Reservo, Privacy Officer
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner. Final policy will be issued before public launch and may differ materially.`;


export const RIDER_AGREEMENT = `RIDER AGREEMENT — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

This Rider Agreement is between you (the "Rider") and David Williams (ABN 50 639 154 277) trading as Reservo ("Reservo"). It applies in addition to the Reservo Terms of Use and Privacy Policy. By booking a ride through the Reservo Platform, you accept this Agreement.

1. THE BOOKING

1.1 Each booking made through the Reservo Platform creates a transport contract between the Rider and the assigned Driver. Reservo is not a party to that contract; Reservo provides the booking and payment infrastructure.

1.2 You must provide accurate pickup and destination details, your correct passenger count, and any requirements (child seat, accessibility, multiple stops, unaccompanied minors) at the time of booking.

1.3 Bookings must be made between 1 hour and 30 days before scheduled pickup. Reservo does not provide on-demand or rank-and-hail rides.

1.4 Your Driver is an independent contractor who holds the driver authorisation and insurance required for point-to-point transport in NSW. Reservo is not the transport provider and is not the insurer; the transport service is covered by the Driver’s insurance.

2. FARES AND PAYMENT

2.1 The fare displayed at booking is binding subject to: (a) variations you authorise (route changes, additional stops, waiting time); (b) tolls and the NSW Passenger Service Levy; (c) GST; and (d) any peak-period surcharge, applied automatically based on the scheduled pickup time and shown in your fare estimate before you confirm.

2.2 You authorise Reservo to charge your nominated payment method on trip completion. Payment processing is performed by Stripe Payments Australia Pty Ltd. If your payment method is declined, Reservo may suspend your account until the outstanding amount is paid or another method is provided.

2.3 Promo codes, referral credits, and wallet credits apply per the conditions displayed at the time of redemption.

2.4 You are entitled to a tax invoice for every trip, available within the Platform.

3. CANCELLATION

3.1 Free cancellation more than 24 hours before scheduled pickup. Within 24 hours, a $10 cancellation fee applies. Within 30 minutes of scheduled pickup, or after the Driver has departed, a $25 cancellation fee applies plus any toll or distance the Driver has incurred. If your trip is cancelled, the NSW Passenger Service Levy is not payable and any portion charged for the levy is fully refunded.

3A. MULTI-STOP BOOKINGS AND ADDITIONAL PASSENGERS

3A.1 You may book a trip with up to 4 intermediate stops between pickup and destination. Each intermediate stop must be either an additional passenger pickup or an additional passenger drop-off.

3A.2 A 1-minute free dwell buffer applies at each intermediate stop. Beyond 1 minute, a charge of $0.50 per minute applies and is added to the final fare.

3A.3 Where you book a stop to pick up an additional passenger ("co-rider"), you:
  (a) provide the co-rider's first name at booking (Reservo does not collect the co-rider's phone number or any further personal information);
  (b) are responsible for ensuring the co-rider is at the pickup point at the scheduled time;
  (c) are responsible for the co-rider's conduct in the vehicle, including any damage, breach of these terms, or cancellation/no-show fees arising from their non-attendance;
  (d) acknowledge that you must remain in the vehicle until all co-rider pickups are complete, so that you may verify each co-rider's identity to the Driver in person.

3A.4 If a co-rider does not arrive within 5 minutes of the Driver reaching their pickup point, the Driver may proceed to the next stop. A $10 stop no-show fee is added to the booking. If you ask the Driver to wait beyond 5 minutes, a wait charge of $0.50 per minute applies.

3A.5 You may add, remove, or reorder stops during the trip. Each change recalculates the fare for your confirmation, and the Driver must accept the change within 60 seconds. A maximum of 3 mid-trip changes per booking applies.

3A.6 Unaccompanied minors are not supported. If any passenger or co-rider on the booking is under 18 years of age, you (or another adult on the booking) must travel in the vehicle with them.

3B. RECURRING BOOKINGS

3B.1 You may book a series of trips on a recurring schedule. Each individual trip in a recurring booking ("occurrence") is treated as a separate transaction for the purposes of this Agreement, including fare, cancellation rules, NSW Passenger Service Levy, and driver matching.

3B.2 Recurring bookings may be scheduled up to 12 weeks in advance. Each occurrence's fare is calculated based on its specific date, time, and route and is locked at the time of series creation. Fare calculation is asynchronous and may take up to a few minutes after you confirm the series.

3B.3 Payment for each occurrence is processed 2 hours before its scheduled pickup time. If the payment method fails, you will be notified immediately and have until 30 minutes before pickup to update your payment method. Failure to resolve a declined payment by that time results in that occurrence being cancelled. Other occurrences in the series are not affected.

3B.4 You may cancel an individual occurrence at any time, subject to the standard cancellation rules (clause 3). You may cancel all remaining occurrences in a series, in which case standard cancellation rules apply to each future occurrence individually based on its scheduled pickup time relative to the cancellation request. Occurrences more than 24 hours away may be cancelled free of charge.

3B.5 The Driver who accepts the first occurrence in your series becomes the "preferred Driver" for subsequent occurrences and is offered each occurrence first. If they decline or are unavailable, the occurrence falls back to standard Driver matching. You may be informed of any Driver substitution.

3B.6 You may modify a single occurrence (e.g. change time or address) or all future occurrences in the series. Modifications trigger re-pricing of the affected occurrence(s), and you will be asked to confirm the new fare.

3B.7 If your payment method on file expires while you have active recurring bookings, you will be notified at least 7 days before expiry. If you have not updated the payment method by expiry, subsequent occurrences are paused. If still unresolved after 14 further days, the remaining series is automatically cancelled.

3B.8 NSW public holidays may be skipped automatically if you choose this option when creating the series. The NSW public holiday list is maintained by Reservo and applied at the time of each occurrence.

3C. MULTI-VEHICLE BOOKINGS

3C.1 You may book up to 4 vehicles in a single trip ("vehicle group"). Each vehicle in the group is dispatched independently with its own Driver, Trip PIN, fare, and NSW Passenger Service Levy. The vehicle group shares the same pickup location, scheduled pickup time, and final destination.

3C.2 As the booking rider, you are the payer for all vehicles in the group and are charged separately for each vehicle's fare. You do not need to travel in any of the vehicles, but you may travel in one if you choose. Other passengers (including family members, employees, or guests) may travel in the vehicles you have booked, and you are responsible for ensuring each passenger group is at the pickup point on time.

3C.3 Drivers are matched to each vehicle in parallel. If after 90 seconds not all vehicles have been confirmed, an extended 5-minute search applies. If at the end of the extended window not all vehicles have been confirmed, you will be asked to choose between:
  (a) waiting an additional 5 minutes while we continue searching;
  (b) proceeding with the confirmed vehicles only (unconfirmed vehicles are cancelled with no fee); or
  (c) cancelling the entire booking with no fee. In this case, Drivers who had already confirmed receive a standby compensation payment.

3C.4 You may cancel an individual vehicle within a group without affecting the others. Standard cancellation rules apply to the cancelled vehicle. The remaining vehicles continue.

3C.5 You may cancel the entire group at any time. Standard cancellation rules apply to each vehicle in the group individually based on its scheduled pickup time relative to the cancellation request.

3C.6 You may also modify an individual vehicle (e.g. update its destination or special instructions) without affecting other vehicles in the group. Each modification triggers fare recalculation for that vehicle only.

3C.7 Unaccompanied minors are not supported in multi-vehicle bookings. If any vehicle in the group is to carry minor passenger(s), an adult on the booking must travel in that vehicle.

3C.8 Each vehicle in the group is treated as a separate transaction for the NSW Passenger Service Levy, fare, cancellation rules, driver matching, and rewards. Drivers earn or lose points based on their own vehicle's outcome independently of other vehicles in the group.

3D. PASSENGERS WITH SPECIFIC NEEDS

3D.1 You may indicate at the time of booking, or on your Rider profile, if you require additional support during the trip. Categories include accessibility needs, mobility aids, guide or support animals, cognitive or communication support, and child seats.

3D.2 Drivers are trained in disability awareness and are required to accept passengers travelling with a guide or support animal at no additional charge. If a Driver refuses service to a passenger with a guide or support animal, you may submit a formal complaint via the in-app complaints process; the matter may also be referred to the NSW Point to Point Transport Commissioner.

3D.3 You may provide an emergency contact for a specific trip. This contact will only be used by Reservo if there is an incident or safety event during the trip.

3D.4 If you are a minor (under 18 years of age), you must be accompanied by an adult passenger on the booking. Unaccompanied minors are not supported.

3E. INCIDENTS AND COMPLAINTS

3E.1 If a safety incident, collision, injury, assault, or other serious event occurs during your trip, you may report it through the in-app incident reporting form. Serious incidents are classified as Notifiable Occurrences under the NSW Point to Point Transport Act 2014 and are reported by Reservo to the NSW Point to Point Transport Commissioner within statutory timeframes.

3E.2 You may submit a formal complaint through the in-app complaints process. Reservo will acknowledge complaints within 24 hours, provide an initial response within 7 days, and aim to resolve complaints within 28 days. Serious complaints may be referred to the NSW Point to Point Transport Commissioner.

3E.3 In an emergency, call 000 first. The in-app SOS button alerts Reservo's operations team and may, depending on circumstance, also share your location with emergency services.

3E.4 Reservo retains records of all incidents, complaints, and consents for at least 7 years as required by NSW Point to Point Transport regulations, or for longer as required by law.

3F. NO-SHOW AND DRIVER FAILURE

3F.1 No-show: if you fail to arrive at the pickup point within 15 minutes of the scheduled time after the Driver has arrived and waited (with the first 5 minutes free), the Driver may declare a no-show and you may be charged the full fare.

3F.2 If the Driver cancels or fails to arrive, you will not be charged and may be entitled to alternative arrangements through the Platform.

3F.3 Full details are in the Reservo Cancellation and Refund Policy.

3G. LOST ITEMS AND RETURN FEES

3G.1 If you leave an item in your Driver's vehicle, you may report it via the Reservo app within 14 days of trip completion. Your Driver has 48 hours to respond by either confirming they have the item, indicating that you need to collect it, or stating they cannot find it.

3G.2 If the Driver has your item and offers to return it, you may offer them a return fee of a minimum of $30 (and up to a maximum of $200). The Driver may accept or decline this offer. If accepted, the fee will be charged to your saved payment method and the Driver has 72 hours to deliver the item. You must confirm receipt of the item within 7 days to release payment to the Driver; failure to confirm within 7 days results in automatic confirmation and release of payment.

3G.3 If the Driver declines the offered fee, or you prefer to collect the item, you may coordinate a pickup directly via in-app messages on the original trip and book a delivery trip to the agreed location.

3G.4 The Driver is only required to safeguard your item for 14 days from the time they confirm possession ("custody period"). After this 14-day custody period expires, the Driver is released of any further obligation and may dispose of the item at their discretion. You will be notified before custody expires.

3G.5 If the Driver fails to deliver the item within 72 hours after you have paid the return fee, you may request a refund through the Reservo app and Reservo will investigate.

3H. DAMAGE AND CLEANING CLAIMS AGAINST RIDERS

3H.1 Drivers may submit a damage or cleaning claim against you within 48 hours of trip completion if your conduct in or on the vehicle results in physical damage or requires cleaning beyond normal wear. All claims must include a minimum of 3 photographs and a detailed description.

3H.2 Reservo will review all claims, typically within 48 hours of submission. Claims may be approved in whole, approved in part for a lesser amount, or rejected entirely.

3H.3 If approved, the approved amount will be charged to your saved payment method via Stripe. You will receive notification of the charge along with a copy of the Driver's claim description and supporting photographs.

3H.4 If you disagree with a damage claim charge, you may submit a formal complaint through the Reservo support system within 7 days of the charge. Reservo will investigate and may issue a refund where appropriate.

3I. PHOTO EVIDENCE AND IMAGE PRIVACY

3I.1 When you submit photographs through the Reservo app (including in lost item reports, complaints, or other support matters), you grant Reservo a non-exclusive licence to store, transmit, and share those photographs with relevant parties for the purpose of resolving the matter. Relevant parties may include your Driver, Reservo administrators, and (where applicable) lawful authorities.

3I.2 Reservo will not share your submitted photographs with third parties for marketing or any purpose unrelated to the matter for which they were submitted.

3I.3 Photographs linked to closed cases are retained for 7 years for audit and regulatory purposes, after which they are permanently deleted.

4. CONDUCT IN THE VEHICLE
4.1 You agree to:
  (a) wear seatbelts as required by NSW Road Rules;
  (b) treat the Driver with courtesy and refrain from abusive, threatening, harassing, or discriminatory behaviour;
  (c) not consume alcohol, illicit drugs, or smoke in the vehicle;
  (d) not transport dangerous, illegal, or hazardous items;
  (e) not damage the vehicle (including soiling, vomiting, or breakage);
  (f) supervise children and pets in your party.

4.2 Damage and cleaning: if you damage or soil the vehicle, you may be charged a cleaning or damage fee in line with the Reservo damage claim process. You may dispute the claim through the Platform.

4.3 Lost items: report lost items via the Platform. Reservo facilitates the return process but does not guarantee recovery.

5. SAFETY

5.1 Trip PIN: every trip has a unique PIN known only to you and the Driver. Verify the Driver's vehicle and confirm their identity before sharing the PIN.

5.2 GPS tracking, in-app messaging, and SOS: Reservo provides safety features within the app. In an emergency, contact 000 first.

5.3 Audio recording: Reservo does not record audio during trips. Should this position change in the future, you will be notified and consent will be re-collected before any recording occurs.

6. RATINGS AND REVIEWS

6.1 You may rate and review your Driver after the trip. Ratings and reviews must be honest, factual, and respectful. Defamatory, abusive, or knowingly false reviews may be removed and may result in account suspension.

7. DISPUTES

7.1 If you have a complaint, raise it via the Platform or admin@reservo.au within a reasonable time of the trip.

7.2 The dispute resolution process is set out in the Terms of Use.

8. ACCOUNT SUSPENSION

8.1 Reservo may suspend or terminate your Rider account for breach of this Agreement, the Terms of Use, or applicable law.

9. CONSUMER GUARANTEES

9.1 Nothing in this Agreement excludes, restricts, or modifies any consumer guarantee, right, or remedy under the Australian Consumer Law that cannot be excluded.

10. CONTACT

David Williams trading as Reservo
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner.`;


export const DRIVER_AGREEMENT = `DRIVER AGREEMENT — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

This Driver Agreement is between you (the "Driver") and David Williams (ABN 50 639 154 277) trading as Reservo ("Reservo"). By accepting bookings through the Reservo Platform you accept this Agreement.

1. INDEPENDENT CONTRACTOR RELATIONSHIP

1.1 You provide transport services as an independent contractor. You are not an employee, agent, partner, or joint venturer of Reservo.

1.2 You are responsible for your own:
  (a) Australian Business Number (ABN);
  (b) tax obligations, including income tax, GST registration if required, BAS lodgement, and PAYG;
  (c) superannuation;
  (d) work-cover or equivalent personal insurance arrangements;
  (e) compulsory third-party (CTP) insurance, comprehensive insurance, and any commercial-use endorsements required for point-to-point operation;
  (f) NSW Driver Authorisation under the Point to Point Transport (Taxis and Hire Vehicles) Act 2016, including current Working With Children Check where applicable;
  (g) vehicle authorisation, registration, roadworthiness, and maintenance.

1.3 You acknowledge that Reservo is a Booking Service Provider, not a transport operator, and that the transport contract is between you and the Rider.

2. ELIGIBILITY AND ONBOARDING

2.1 To drive on the Platform you must:
  (a) hold a current NSW driver licence (Class C or higher as required);
  (b) hold a current NSW Driver Authorisation;
  (c) operate an authorised vehicle that meets the Platform's tier requirements;
  (d) maintain valid CTP and commercial vehicle insurance;
  (e) complete the Reservo onboarding process, including identity verification, document review, and training modules;
  (f) be approved by Reservo's compliance team.

2.2 You must keep all credentials, licences, and authorisations current. You must notify Reservo within 24 hours if any of them are suspended, cancelled, or expire.

3. ACCEPTING BOOKINGS

3.1 You may accept or decline any booking offered to you. Reservo does not guarantee any minimum number of bookings.

3.2 Once you accept a booking, you commit to perform it. Cancellation of a confirmed booking — particularly within 2 hours of pickup — may result in:
  (a) a cancellation penalty fee of $10 (cancelled within 2 hours of pickup) or $25 (cancelled within 30 minutes or after departing for the pickup);
  (b) a 5-point deduction from your annual rewards points for cancellations within 2 hours of scheduled pickup;
  (c) a temporary cool-off preventing you from being matched to that Rider for 24 hours;
  (d) impact on your quality tier (gold, silver, bronze, or probation).

3.3 You must arrive at the pickup point at the scheduled time and complete the trip safely and lawfully.

4. FARES AND PAYOUTS

4.1 Reservo collects the fare from the Rider on your behalf. Reservo deducts a platform commission of 20%, the NSW Passenger Service Levy, GST as applicable, and any deductions for cancellation penalties or damage claims, and remits the balance to you.

4.2 Payouts are processed via Stripe Connect to your nominated Australian bank account. You are responsible for keeping your payout details accurate.

4.3 You are responsible for issuing tax invoices to Reservo or to Riders as applicable, and for your own tax compliance.

5. STANDARDS OF SERVICE

5.1 You must:
  (a) drive safely and lawfully, including compliance with NSW Road Rules and fatigue management requirements (10 hours per day, 50 hours per week, 6 consecutive day limit);
  (b) maintain the vehicle in clean, mechanically sound, and roadworthy condition;
  (c) treat Riders with respect and refrain from abusive, threatening, harassing, or discriminatory behaviour;
  (d) refuse to drive while affected by alcohol, drugs, fatigue, or illness;
  (e) carry and use child seats lawfully where booked;
  (f) accommodate accessibility needs you have agreed to support;
  (g) complete pre-trip checklists and safety incident reports as required;
  (h) cooperate with Reservo's safety and complaints processes and with regulatory authorities.

6. REWARDS PROGRAM

6.1 Reservo operates a points-based annual profit-share program. The full rules are published in the Platform under "My Rewards". Key provisions:
  (a) trip points are awarded per completed trip based on your cancellation rate at trip start: 5 points if rate is below 1%, 3 points if 1% to under 2%, 2 points if 2% to under 3%, 1 point if 3% to under 4%, 0 points if 4% or higher;
  (b) referral bonus of $5 plus 5 points when a Rider you referred completes their first trip;
  (c) penalty of 5 points per cancellation made within 2 hours of scheduled pickup;
  (d) at the end of each Australian financial year (1 July to 30 June), 75% of Reservo's net profit (after all expenses and taxes) is distributed to Drivers based on points earned in that year, in proportion to each Driver's share of total points;
  (e) the program rules, percentages, and points buckets may be amended by Reservo from time to time, with at least 30 days' notice.

6.2 Participation in the rewards program does not give rise to a fixed entitlement. Distribution depends on Reservo's annual financial performance, which Reservo determines in good faith. In any year where Reservo does not generate net profit, no distribution is made.

7. CONFIDENTIALITY

7.1 You agree not to use Rider personal information for any purpose other than performing the trip. You must not contact Riders outside the Platform, take bookings off-platform, or use Rider data for marketing.

8. SAFETY AND INCIDENTS

8.1 You must immediately report to Reservo any:
  (a) accident or near-miss involving the vehicle while on a Reservo trip;
  (b) safety incident or threat involving a Rider;
  (c) damage to the vehicle by a Rider;
  (d) lost-property report;
  (e) request from a regulatory or law-enforcement body relating to a trip.

8.2 You consent to Reservo retaining trip telemetry, ratings, and complaint records for the purposes set out in the Privacy Policy.

8A. COMPLIANCE AND CONSENTS

8A.1 You acknowledge and consent to:
  (a) Reservo's verification of your NSW Point to Point Driver Authorisation (PT Driver Authorisation Number) with the NSW Point to Point Transport Commissioner;
  (b) periodic checks of your driving record, national criminal history, Working With Children Check (where applicable), and medical fitness;
  (c) data sharing between Reservo and the NSW Point to Point Transport Commissioner as required by law, including but not limited to trip records, incident reports, and complaints made about you;
  (d) the Driver Vehicle Dashboard (DVD) requirements, including any vehicle inspections, in-service compliance checks, and electronic record-keeping obligations.

8A.2 You agree to report all Notifiable Occurrences (as defined by the Commissioner) using the in-app incident reporting form within the statutory timeframes (typically 24 hours for serious incidents, 7 days for less serious matters). Failure to report a Notifiable Occurrence is a serious matter and may result in suspension, termination of your driver status, and statutory consequences.

8A.3 You agree that:
  (a) Reservo will retain consent records, incident reports, and complaint records for at least 7 years;
  (b) any complaint made about you will be investigated, and you will be given the opportunity to respond;
  (c) serious matters may be referred to the NSW Point to Point Transport Commissioner.

8A.4 You agree to accept Riders travelling with a guide or support animal at no additional charge. Refusal to do so may constitute discrimination under the Disability Discrimination Act 1992 (Cth) and the Anti-Discrimination Act 1977 (NSW), and may result in immediate suspension and reporting to the relevant authority.

8B. LOST ITEM CUSTODY AND RETURN

8B.1 If a Rider reports an item left in your vehicle, you will be notified through the Reservo app and must respond within 48 hours. Failure to respond within 48 hours escalates the matter to Reservo administration and may impact your account standing.

8B.2 You must respond by:
  (a) confirming the item is in your possession and offering to return it;
  (b) confirming the item is in your possession but indicating the Rider must collect it; or
  (c) confirming that, having checked your vehicle, the item is not present.

8B.3 If you confirm possession and agree to return the item personally, the Rider may offer a return fee of a minimum of $30. You may accept this offer (in which case the Rider's payment method is charged, you must deliver the item within 72 hours, and you receive 100% of the fee on Rider confirmation — Reservo does not take a commission on return fees) or decline the offer (in which case the Rider must arrange to collect the item from you).

8B.4 You are responsible for the safekeeping of lost items for 14 days from the time you confirm possession ("custody period"). After this 14-day custody period expires, you are released of any further obligation under this clause and may dispose of unclaimed items at your discretion. Reservo will notify the Rider before custody expiry.

8B.5 If you fail to deliver an item within 72 hours after a return fee has been paid, the Rider may request a refund and the matter will be reviewed by Reservo administration. Repeated failure to deliver paid returns may affect your account standing.

8B.6 You may also proactively report items found in your vehicle through the Reservo app, even before a Rider has reported them missing. The same custody and return rules apply from the time of your report.

8C. DAMAGE AND CLEANING CLAIMS

8C.1 If a Rider's conduct results in physical damage to your vehicle or required cleaning beyond normal wear, you may submit a damage or cleaning claim through the Reservo app within 48 hours of trip completion. Claims submitted outside this window may be rejected.

8C.2 Every claim must include:
  (a) a minimum of 3 photographs clearly showing the damage or cleaning required, from multiple angles where possible;
  (b) a detailed written description of what happened, including the location in the vehicle affected;
  (c) the amount claimed, which must be reasonable and supported by evidence (receipts or quotes are encouraged where applicable).

8C.3 Reservo administration will review each claim, typically within 48 hours. Reservo may approve in full, approve a lesser amount, or reject the claim. The approved amount is charged to the Rider's saved payment method.

8C.4 You will receive 100% of the approved amount as compensation. Reservo does not take any commission on damage claim compensation.

8C.5 If a charge to the Rider's payment method fails, Reservo administration will attempt to retry the charge or pursue the matter with the Rider directly. Payout to you occurs once the charge is successfully captured.

8C.6 You may withdraw a claim at any time before Reservo administration has issued a decision.

8C.7 False, fraudulent, or grossly exaggerated damage claims are a serious breach of this Agreement and are grounds for immediate suspension or termination from the Reservo platform, and may be reported to relevant authorities.

8D. INCIDENT PHOTOGRAPHIC EVIDENCE

8D.1 For incidents requiring NSW Point to Point Transport Commissioner notification (including collisions, serious injuries, vehicle damage, or safety concerns), photographic evidence may be required for certain incident types. You must submit such photos through the in-app uploader as part of the incident report. Reservo retains incident-related photographs for the period required by law.

9. SUSPENSION AND TERMINATION
9.1 Reservo may suspend or terminate your driver account immediately for:
  (a) loss of driver authorisation, vehicle authorisation, insurance, or working-with-children check;
  (b) breach of safety standards;
  (c) Rider safety complaints assessed as substantiated;
  (d) fraud, off-platform contracting, or abuse of the rewards system;
  (e) criminal charges relevant to safety;
  (f) breach of this Agreement, the Terms of Use, or applicable law.

9.2 You may terminate this Agreement and close your driver account at any time by giving notice to admin@reservo.au, subject to completing any active bookings.

10. INDEMNITY

10.1 You indemnify Reservo against any loss, damage, claim, or cost arising out of:
  (a) your performance or non-performance of trips;
  (b) breach of your obligations under this Agreement, applicable law, or your driver authorisation conditions;
  (c) damage caused by you or your vehicle to any person or property;
  (d) your tax, superannuation, and insurance obligations.

11. LIMITATION OF LIABILITY

11.1 To the maximum extent permitted by law, Reservo's liability to you under this Agreement, in any 12-month period, is limited to the platform commission paid by you in that period.

12. DISPUTES AND APPEALS

12.1 If you dispute a fare, deduction, suspension, or rewards-program decision, raise the matter with Reservo via admin@reservo.au within 30 days. Reservo will respond within 14 days.

12.2 If unresolved, the dispute resolution process in the Terms of Use applies.

13. GOVERNING LAW

13.1 This Agreement is governed by the laws of New South Wales. The parties submit to the exclusive jurisdiction of the courts of New South Wales.

14. CONTACT

David Williams trading as Reservo
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner.`;


export const AUDIO_RECORDING_CONSENT = `AUDIO RECORDING POLICY — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

This document explains Reservo's current position on audio recording during trips.

1. CURRENT POSITION

1.1 Reservo does not record audio during trips. No microphones, voice recorders, or audio capture devices are operated by Reservo or required to be operated by Drivers in connection with the Platform.

1.2 Drivers are not authorised to record audio of Riders during a Reservo trip without the Rider's express prior consent and compliance with the Surveillance Devices Act 2007 (NSW). Recording without consent is a criminal offence and may result in immediate termination of the Driver's account and referral to NSW Police.

2. IF THIS CHANGES IN THE FUTURE

2.1 Reservo may consider introducing optional in-vehicle audio recording for safety and dispute-resolution purposes in the future.

2.2 If Reservo decides to do so, the following safeguards will be implemented before any recording occurs:
  (a) this document will be revised and reviewed by an Australian-qualified legal practitioner with expertise in the Surveillance Devices Act 2007 (NSW) and the Privacy Act 1988 (Cth);
  (b) Riders and Drivers will be notified at least 30 days in advance;
  (c) explicit, separate consent will be collected from both Riders and Drivers before any recording is made;
  (d) recordings will be limited in scope (active trips only), encrypted at rest, access-controlled, and retained only as long as necessary;
  (e) Riders will have the right to refuse recording and to book unrecorded trips instead.

3. RIDER AND DRIVER RIGHTS

3.1 If you become aware of any unauthorised recording during a Reservo trip, please:
  (a) contact NSW Police if you believe an offence has been committed;
  (b) report it to admin@reservo.au so that Reservo can investigate and take action against the Driver's account.

4. PRIVACY COMPLAINTS

4.1 Privacy concerns are handled per the Reservo Privacy Policy. You may also complain to the Office of the Australian Information Commissioner (oaic.gov.au, 1300 363 992).

5. CONTACT

David Williams trading as Reservo
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner. The Surveillance Devices Act 2007 (NSW) sets strict rules around recording private conversations. If Reservo's position on audio recording changes in the future, this document and the underlying technical implementation must be reviewed by qualified legal counsel before any feature is enabled.`;


export const CORPORATE_ACCOUNT_TERMS = `CORPORATE ACCOUNT TERMS — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

These Corporate Account Terms apply to organisations ("Corporate Account Holder") that establish a corporate account with David Williams (ABN 50 639 154 277) trading as Reservo ("Reservo") for staff, contractors, or guests to use the Reservo Platform under consolidated billing.

1. ELIGIBILITY

1.1 Corporate accounts are available to Australian businesses, not-for-profits, government entities, and other organisations holding a valid ABN and verified contact details.

1.2 Reservo may at its discretion accept or decline corporate account applications.

2. AUTHORISED USERS

2.1 The Corporate Account Holder nominates a Primary Administrator who manages the account.

2.2 The Primary Administrator may invite individual Riders ("Authorised Users") to book under the corporate account, subject to spend limits, allowed routes, and other policies set by the Administrator.

2.3 The Corporate Account Holder is responsible for ensuring that all Authorised Users:
  (a) accept the Reservo Terms of Use, Privacy Policy, and Rider Agreement;
  (b) use the corporate account for legitimate business purposes only;
  (c) do not share their account credentials.

3. BILLING

3.1 All trips booked by Authorised Users under the corporate account are billed to the corporate payment method on file (or invoiced monthly, where Reservo agrees) within the agreed spending limits.

3.2 The Corporate Account Holder is liable for all charges incurred under the account, including unauthorised use that resulted from inadequate credential security or failure to revoke departing users' access.

3.3 GST tax invoices are issued in the corporate name.

4. SPEND CONTROLS

4.1 The Primary Administrator may set:
  (a) per-user monthly spending limits;
  (b) allowed pickup or destination zones;
  (c) allowed booking times (e.g. business hours only);
  (d) allowed service tiers.

4.2 Trips that exceed configured limits are blocked at booking time.

5. AUTHORISED USERS' DATA

5.1 The Corporate Account Holder acknowledges that Reservo collects and processes personal information about Authorised Users in line with the Privacy Policy, and is itself subject to the Privacy Act 1988 (Cth) and APPs in handling that information.

5.2 The Primary Administrator may see aggregated trip data and itemised invoices, including pickup/destination, time, fare, and rider name. Reservo does not share message content or detailed location traces with the Administrator without lawful basis.

6. TERMINATION

6.1 The Corporate Account Holder may close the account at any time by giving 14 days' notice in writing. Reservo will issue a final invoice for trips taken up to closure.

6.2 Reservo may terminate the account on breach of these Terms, non-payment, or fraud, with immediate effect.

7. LIABILITY AND INDEMNITY

7.1 The Corporate Account Holder indemnifies Reservo against any loss arising from misuse of the corporate account by its Authorised Users.

7.2 Reservo's liability is limited as set out in the Terms of Use.

8. GOVERNING LAW

8.1 These Terms are governed by the laws of New South Wales.

9. CONTACT

David Williams trading as Reservo
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner.`;


export const CANCELLATION_REFUND_POLICY = `CANCELLATION AND REFUND POLICY — RESERVO

Effective date: 08/05/2026
Last updated: 08/05/2026
Status: DRAFT — subject to legal review prior to public launch

This policy explains when fares and fees are charged or refunded. It forms part of the Reservo Terms of Use.

1. RIDER CANCELLATION

1.1 More than 24 hours before scheduled pickup: free cancellation, full refund of any pre-charged amount.

1.2 Within 24 hours of scheduled pickup: a cancellation fee of $10 applies. The remaining fare is refunded.

1.3 Within 30 minutes of scheduled pickup, or after the Driver has departed for the pickup: a cancellation fee of $25 applies, plus any toll or distance the Driver has incurred. The Driver receives a portion of this fee.

2. NO-SHOW

2.1 If the Rider fails to arrive at the pickup point within 15 minutes after the Driver has arrived (with the first 5 minutes free of waiting charges, then waiting fees apply), the Driver may declare a no-show. The full fare may be charged.

3. DRIVER CANCELLATION

3.1 If the Driver cancels or fails to arrive, the Rider is not charged. Reservo will, where possible, offer an alternative Driver.

3.2 Drivers cancelling within 2 hours of scheduled pickup may incur a penalty as set out in the Driver Agreement, and any compensation arrangements offered to affected Riders are funded from those penalties.

4. PLATFORM-CAUSED CANCELLATION

4.1 If Reservo cancels a booking due to a platform fault, no charge applies and Reservo will provide reasonable assistance, which may include alternative arrangements or wallet credit.

5. REFUNDS

5.1 Refunds are processed to the original payment method by Stripe Payments Australia Pty Ltd within 5 to 10 business days. Wallet credits remain in your wallet and may be used on future trips, subject to the wallet terms.

6. DISPUTES

6.1 Fare and refund disputes are raised via admin@reservo.au within 30 days of the trip. Reservo investigates and responds within 14 days.

6.2 You retain all consumer rights under the Australian Consumer Law that cannot be excluded.

7. CONTACT

David Williams trading as Reservo
Email: admin@reservo.au
Phone: 0435 679 630
Address: Unit 6/297 Miller Road, Bass Hill NSW 2197

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW. It has not been reviewed by an Australian-qualified legal practitioner.`;